import { useState } from "react";
import {
  setQualityOverride,
  setReducedMotionOverride,
  getQualityOverride,
  getReducedMotionOverride,
} from "../lib/capability";
import type { DeviceTier } from "../scenario/types";

/**
 * Start-screen quality + accessibility preferences. Persisted via
 * capability.ts overrides (localStorage), so a choice made once sticks
 * across courses and reloads. Lets the course run smoothly on a slow PC or
 * weak connection (down to "Low") and lets any learner turn off camera
 * motion regardless of what their OS advertises.
 */
const PRESETS: { key: DeviceTier | "auto"; label: string; hint: string }[] = [
  { key: "auto", label: "Auto", hint: "Detect for me" },
  { key: "high", label: "High", hint: "Best visuals" },
  { key: "mid", label: "Balanced", hint: "Recommended" },
  { key: "low", label: "Low", hint: "Slow PC / connection" },
];

export function QualityPrefs({
  onChange,
}: {
  onChange: (next: { tier: DeviceTier | null; reducedMotion: boolean | null }) => void;
}) {
  const [preset, setPreset] = useState<DeviceTier | "auto">(() => getQualityOverride() ?? "auto");
  const [motion, setMotion] = useState<boolean>(() => getReducedMotionOverride() ?? false);
  const [motionTouched, setMotionTouched] = useState<boolean>(() => getReducedMotionOverride() !== null);

  return (
    <div className="qprefs">
      <div className="qprefs-row">
        <span className="qprefs-label">Graphics quality</span>
        <div className="qprefs-presets" role="radiogroup" aria-label="Graphics quality">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`qprefs-btn ${preset === p.key ? "on" : ""}`}
              role="radio"
              aria-checked={preset === p.key}
              onClick={() => {
                setPreset(p.key);
                const tier = p.key === "auto" ? null : p.key;
                setQualityOverride(tier);
                onChange({ tier, reducedMotion: motionTouched ? motion : null });
              }}
            >
              <span className="qprefs-btn-label">{p.label}</span>
              <span className="qprefs-btn-hint">{p.hint}</span>
            </button>
          ))}
        </div>
      </div>

      <label className="qprefs-motion">
        <input
          type="checkbox"
          checked={motion}
          onChange={(e) => {
            const v = e.target.checked;
            setMotion(v);
            setMotionTouched(true);
            setReducedMotionOverride(v);
            const tier = preset === "auto" ? null : preset;
            onChange({ tier, reducedMotion: v });
          }}
        />
        <span>Reduce camera motion (shorter, gentler moves between scenes)</span>
      </label>
    </div>
  );
}
